/**
 * Utility functions for scroll position management and scrolling to elements in the reader
 */

export interface ScrollPosition {
  x: number;
  y: number;
}

export interface ScrollToOptions {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
  inline?: ScrollLogicalPosition;
}

/**
 * Gets the current scroll position of the window
 * @returns Object with x and y scroll coordinates
 */
export function getScrollPosition(): ScrollPosition {
  return {
    x: window.pageXOffset || document.documentElement.scrollLeft,
    y: window.pageYOffset || document.documentElement.scrollTop,
  };
}

/**
 * Sets the scroll position of the window
 * @param position - The scroll position to set
 */
export function setScrollPosition(position: ScrollPosition): void {
  window.scrollTo(position.x, position.y);
}

/**
 * Restores scroll position after an operation that might change it
 * @param originalPosition - The position to restore
 */
export function restoreScrollPosition(originalPosition: ScrollPosition): void {
  requestAnimationFrame(() => {
    setScrollPosition(originalPosition);
  });
}

/**
 * Scrolls to a highlight element by its ID
 * @param highlightId - The ID of the highlight to scroll to
 * @param options - Scroll behavior options
 */
export function scrollToHighlight(
  highlightId: string,
  options: ScrollToOptions = {},
): void {
  const defaultOptions: ScrollToOptions = {
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  };

  const scrollOptions = { ...defaultOptions, ...options };

  const highlightSpan = document.querySelector(
    `[data-id="${highlightId}"]`,
  ) as HTMLElement;
  if (highlightSpan) {
    highlightSpan.scrollIntoView(scrollOptions);
  }
}

/**
 * Scrolls to a key sentence element by its ID
 * @param keySentenceId - The ID of the key sentence to scroll to
 * @param options - Scroll behavior options
 */
export function scrollToKeySentence(
  keySentenceId: string,
  options: ScrollToOptions = {},
): void {
  const defaultOptions: ScrollToOptions = {
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  };

  const scrollOptions = { ...defaultOptions, ...options };

  const keySentenceSpans = document.querySelectorAll(
    `[data-key-sentence="${keySentenceId}"]`,
  );

  if (keySentenceSpans.length > 0) {
    const firstSpan = keySentenceSpans[0] as HTMLElement;
    firstSpan.scrollIntoView(scrollOptions);
  }
}

/**
 * Scrolls to a selected element within a container
 * @param containerRef - Reference to the container element
 * @param options - Scroll behavior options
 */
export function scrollToSelectedElement(
  containerRef: React.RefObject<HTMLElement>,
  options: ScrollToOptions = {},
): void {
  const defaultOptions: ScrollToOptions = {
    behavior: 'smooth',
    block: 'nearest',
    inline: 'nearest',
  };

  const scrollOptions = { ...defaultOptions, ...options };

  if (containerRef.current && typeof window !== 'undefined') {
    const selectedElement = containerRef.current.querySelector('[data-selected="true"]');
    if (selectedElement && 'scrollIntoView' in selectedElement) {
      selectedElement.scrollIntoView(scrollOptions);
    }
  }
}
